import { DOCUMENT, ErrorHandler, Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationError } from '@angular/router';

const MODULE_RECOVERY_KEY = 'musaffa.module-load-recovery';
const MODULE_RECOVERY_COOLDOWN_MS = 60_000;
const MODULE_LOAD_ERROR = /Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandlerService implements ErrorHandler {
  private isBrowser: boolean;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  handleError(error: unknown): void {
    this.reportError(error);
  }

  handleNavigationError(event: NavigationError): void {
    this.reportError(event.error, event.url);
  }

  private reportError(error: unknown, targetUrl?: string): void {
    const message = error instanceof Error ? error.message :
      typeof error === 'object' && error !== null && 'message' in error
        ? String(error.message) : String(error);

    // Suppress known non-fatal Firebase Auth SSR errors
    if (message.includes('INTERNAL ASSERTION FAILED: Expected a class definition')) {
      return;
    }

    if (MODULE_LOAD_ERROR.test(message) && this.recoverModuleLoad(targetUrl)) {
      return;
    }
    console.error('An error occurred:', error);
  }

  private recoverModuleLoad(targetUrl?: string): boolean {
    const win = this.isBrowser ? this.document.defaultView : null;
    if (!win || !win.navigator.onLine) return false;

    try {
      // Persist before navigating: the next application instance must not loop
      // if the deployment or network is still serving a missing module.
      const lastAttempt = Number(win.sessionStorage.getItem(MODULE_RECOVERY_KEY));
      const now = Date.now();
      if (lastAttempt && now - lastAttempt < MODULE_RECOVERY_COOLDOWN_MS) return false;

      const destination = targetUrl ? new URL(targetUrl, win.location.origin) : null;
      if (destination && destination.origin !== win.location.origin) return false;

      win.sessionStorage.setItem(MODULE_RECOVERY_KEY, String(now));
      if (destination) {
        win.location.assign(destination.href);
      } else {
        win.location.reload();
      }
      return true;
    } catch {
      // Without persistent storage we cannot safely guard against reload loops.
      return false;
    }
  }
}
